import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ShoppingBag, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Floating live-activity bubble (Fomo / TrustPulse style).
 * Position: fixed bottom-left, site-wide (all pages except admin, checkout,
 * cart, success, restricted admin flows).
 * Cycles through the most recent paid orders every ~7 seconds. Dismissible.
 * Zero PII: first initial + last initial, coarse location, first product name,
 * time-ago. Skips itself on mobile screens < 640px to avoid covering CTAs.
 */

const CYCLE_MS = 7000;
const POLL_MS = 60000;
const DISMISS_KEY = "helixora_recent_orders_dismissed_v1";
const DISMISS_TTL_MS = 60 * 60 * 1000; // reappear after 1 hour

function timeAgo(iso) {
    if (!iso) return "";
    try {
        const diffMs = Date.now() - new Date(iso).getTime();
        const s = Math.max(1, Math.floor(diffMs / 1000));
        if (s < 60) return `${s}s ago`;
        const m = Math.floor(s / 60);
        if (m < 60) return `${m}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        const d = Math.floor(h / 24);
        return `${d}d ago`;
    } catch { return ""; }
}

const HIDDEN_PATH_PREFIXES = ["/admin", "/checkout", "/cart", "/success", "/restricted/login", "/restricted/shop"];

function pathIsHidden(pathname) {
    if (!pathname) return false;
    return HIDDEN_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/") || pathname === p);
}

export function RecentOrdersFloating() {
    const location = useLocation();
    const [orders, setOrders] = useState([]);
    const [idx, setIdx] = useState(0);
    const [dismissed, setDismissed] = useState(false);
    const [hidden, setHidden] = useState(true);
    const [entering, setEntering] = useState(false);

    // Respect dismiss cooldown from localStorage
    useEffect(() => {
        try {
            const raw = localStorage.getItem(DISMISS_KEY);
            if (raw) {
                const ts = parseInt(raw, 10);
                if (Number.isFinite(ts) && Date.now() - ts < DISMISS_TTL_MS) {
                    setDismissed(true);
                    return;
                }
            }
        } catch { /* ignore */ }
        setDismissed(false);
    }, []);

    // Poll public recent orders
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const { data } = await api.get("/orders/recent-public?limit=10");
                if (mounted) setOrders(data.recent || []);
            } catch { /* silent */ }
        };
        load();
        const id = setInterval(load, POLL_MS);
        return () => { mounted = false; clearInterval(id); };
    }, []);

    // Cycle through orders
    useEffect(() => {
        if (orders.length <= 1) return;
        const id = setInterval(() => {
            setEntering(true);
            setTimeout(() => setEntering(false), 400);
            setIdx((i) => (i + 1) % orders.length);
        }, CYCLE_MS);
        return () => clearInterval(id);
    }, [orders.length]);

    // Delayed reveal so it doesn't block page load
    useEffect(() => {
        if (dismissed || pathIsHidden(location.pathname) || orders.length === 0) {
            setHidden(true);
            return;
        }
        const t = setTimeout(() => setHidden(false), 1500);
        return () => clearTimeout(t);
    }, [dismissed, orders.length, location.pathname]);

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        setDismissed(true);
    };

    if (hidden || dismissed) return null;
    const o = orders[idx];
    if (!o) return null;

    // Soften "NEW RESEARCH SHIPMENT" label if the order is more than 3 days old
    // so the widget doesn't feel like fake live social proof.
    let ageDays = 0;
    try {
        ageDays = Math.floor((Date.now() - new Date(o.created_at).getTime()) / (1000 * 60 * 60 * 24));
    } catch { /* ignore */ }
    const eyebrowLabel = ageDays > 3 ? "/ RECENT RESEARCH SHIPMENT" : "/ NEW RESEARCH SHIPMENT";

    return (
        <div
            className="hidden lg:flex fixed bottom-4 left-4 z-40 max-w-[320px] items-start gap-3 p-3 bg-ink-base/95 backdrop-blur border border-neon/30 shadow-lg"
            style={{
                boxShadow: "0 12px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(48,213,152,0.08)",
                animation: entering ? "recent-fade 400ms ease-out" : undefined,
            }}
            data-testid="recent-orders-floating"
        >
            <div className="shrink-0 w-8 h-8 flex items-center justify-center bg-neon/10 border border-neon/30">
                <ShoppingBag className="w-4 h-4 text-neon" strokeWidth={2} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="overline text-[9px] tracking-[0.18em] text-neon mb-1">{eyebrowLabel}</p>
                <p className="text-[13px] text-ink leading-tight">
                    <span className="font-mono font-bold text-neon">{o.display_name}</span> ordered <span className="text-ink">{o.products_summary}</span>
                </p>
                <p className="mt-1.5 text-[10px] font-mono uppercase tracking-wider text-ink-dim truncate">
                    {o.location} · {timeAgo(o.created_at)}
                </p>
            </div>
            <button onClick={dismiss}
                className="shrink-0 p-1 text-ink-mute hover:text-ink transition-colors -mr-1 -mt-1"
                aria-label="Dismiss"
                data-testid="recent-orders-dismiss">
                <X className="w-3.5 h-3.5" />
            </button>
            <style>{`
                @keyframes recent-fade {
                    from { opacity: 0.3; transform: translateY(4px); }
                    to   { opacity: 1;   transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}
